import { BreakpointObserver, BreakpointState, Breakpoints, MediaMatcher } from '@angular/cdk/layout';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { ChangeDetectorRef, Component, ElementRef, Injector, OnDestroy, OnInit, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { ImageGalleryService } from '@services/image-gallery.service';
import { ImageLinkService } from '@services/image-link.service';
import { NgStyle } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {
  BroadcastService,
  ClientConstants,
  ConfigService,
  ControlStateConstants,
  ControlStateSaveService,
  CurrentDocumentService,
  DefaultStateService,
  FileProviderService,
  LocalStorageKeys,
  LocalStorageService,
  ModifiedCSSService,
  NavigationService,
  select
} from '@formbird/services';
import { SharedUrlRoutes } from '@formbird/shared';
import { Observable, Subscription } from 'rxjs';
import { CommandBarComponent } from '../commandbar/command-bar.component';
import { LoadingComponent } from '../loading/loading.component';
import { MenuComponent } from '../menu/menu.component';
import { ThemeToggleComponent } from '../theme-toggle/theme-toggle.component';
import { OfflineStatusComponent } from '../offline-status/offline-status.component';
import { SearchBoxComponent } from '../search/search-box.component';
import { UserComponent } from '../user/user.component';
import { User } from '@formbird/types';

const { serverRoutes } = SharedUrlRoutes;

const SIDEBAR_OPEN_MIN_WIDTH = 1300;

@Component({
  selector: 'ft-top-bar',
  templateUrl: './top-bar.component.html',
  styleUrls: ['./top-bar.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  imports: [CdkScrollable, LoadingComponent, MatToolbarModule, MatButtonModule, MatIconModule, SearchBoxComponent, OfflineStatusComponent, UserComponent, NgStyle, CommandBarComponent, MatSidenavModule, MenuComponent, RouterOutlet, ThemeToggleComponent]
})
export class TopBarComponent implements OnInit, OnDestroy {

  @select(['userState', 'user']) user$: Observable<User>;
  @ViewChild('snav') snav: MatSidenav;
  @ViewChild(SearchBoxComponent) searchBox: SearchBoxComponent | undefined;
  @ViewChild('btnCompactSearch', { read: ElementRef }) btnCompactSearch: ElementRef<HTMLButtonElement> | undefined;

  private route = inject(ActivatedRoute);
  private currentDocumentService = inject(CurrentDocumentService);
  private modifiedCSSService = inject(ModifiedCSSService);
  private configService = inject(ConfigService);
  private navigationService = inject(NavigationService);
  private defaultStateService = inject(DefaultStateService);
  private imageLinkService = inject(ImageLinkService);
  private imageGalleryService = inject(ImageGalleryService);
  private broadcastService = inject(BroadcastService);
  private controlStateSaveService = inject(ControlStateSaveService);
  private breakpointObserver = inject(BreakpointObserver);
  private fileProviderService = inject(FileProviderService);
  private cd = inject(ChangeDetectorRef);
  private localStorageService = inject(LocalStorageService);
  private subscriptions: Subscription = new Subscription();

  initialized = false;

  user: any;

  hideDelete: boolean;
  alert: any;
  displayUIView: boolean;

  hideTopBar = false;
  fullWindow = false;
  noNavBar = false;
  hideGeneralSearch = true;
  isCachingNotAllowed: boolean = false;

  enableDarkMode = false;

  dynamicCSSObj: any = {};
  templateImages: any = {};
  sideBarState: any = {};

  docInfo: any;
  weedLink: any;

  responsiveLayouts: any;
  docHeaderWidth: any;

  homeButtonImage;
  breakpointState;
  fileResources;
  fileResourcesCommandBar;

  defaultCommandBarCSS = {
    background: 'white'
  }

  /** True while the compact (<521px) search view overlays the main toolbar row. */
  compactSearchOpen = false;

  /** Typed search text snapshotted on collapse; restored when the view reopens (session-only). */
  private lastCompactSearchText = '';

  private _mobileQueryListener: () => void;
  public mobileQuery: MediaQueryList;
  private compactQuery: MediaQueryList;
  private _compactQueryListener: () => void;
  private galleryId = 'formbird-top-bar-gallery';
  private router = inject(Router);

  constructor() {
    const injector = inject(Injector);

    this.hideDelete = true;
    this.alert = null;
    this.displayUIView = true;

    this.docHeaderWidth = {};

    this.docInfo = {
      imageLink: this.imageLinkService.imageLink
    };

    const media = injector.get(MediaMatcher);
    const changeDetectorRef = injector.get(ChangeDetectorRef);

    const clientConfig = this.configService.clientConfig();
    this.isCachingNotAllowed = clientConfig?.cachingNotAllowed;

    this.mobileQuery = media.matchMedia('(max-width: 600px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);

    // Same breakpoint as the compact command bar: the search view only exists
    // below 521px, so leaving that range must restore the inline layout
    this.compactQuery = media.matchMedia('(max-width: 520.98px)');
    this._compactQueryListener = () => {
      if (!this.compactQuery.matches && this.compactSearchOpen) {
        this.compactSearchOpen = false;
      }
      changeDetectorRef.detectChanges();
    };
    this.compactQuery.addListener(this._compactQueryListener);
  }

  /**
   * Expands the compact search view over the toolbar row and focuses the
   * search input (compact widths only).
   * @since 5.0.16
   */
  openCompactSearch() {
    this.compactSearchOpen = true;
    this.cd.detectChanges();
    // focus once the 200ms expand transition has revealed the input
    setTimeout(() => {
      const box = this.searchBox;
      const input = box?.getSearchInputElement();
      input?.focus();
      if (box && !input?.value && this.lastCompactSearchText) {
        box.txt = this.lastCompactSearchText;
        box.setTextOnSearchField(this.lastCompactSearchText);
      }
    }, 220);
  }

  /**
   * Collapses the compact search view back to the search icon, returning
   * focus to the icon button. Typed text is kept for the session.
   * @since 5.0.16
   */
  closeCompactSearch() {
    if (!this.compactSearchOpen) {
      return;
    }
    // ng-select's own Escape handling may have already wiped the visible
    // input; the component's txt still holds the typed text at this point
    this.lastCompactSearchText = this.searchBox?.txt || this.searchBox?.getSearchInputElement()?.value || '';
    this.compactSearchOpen = false;
    this.btnCompactSearch?.nativeElement.focus();
  }

  private setCommandBarCSS(commandBarCSS?) {
    this.dynamicCSSObj.commandBarCSS = commandBarCSS || this.configService.clientConfig().commandBarCSS || this.defaultCommandBarCSS;

    if (!this.dynamicCSSObj.commandBarCSS.background && !this.dynamicCSSObj.commandBarCSS['background-color']) {
      this.dynamicCSSObj.commandBarCSS.background = this.defaultCommandBarCSS.background;
    }
  }

  async ngOnInit() {
    const subUser = this.user$.subscribe((user: User) => {
        this.user = user;
    });

    this.subscriptions.add(subUser);

    const routerSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.closeCompactSearch();
      }
    });
    this.subscriptions.add(routerSub);

    const breakPointSub = this.breakpointObserver
      .observe([
        Breakpoints.XSmall
      ])
      .subscribe((state: BreakpointState) => {
        this.breakpointState = state;
        this.setHomeButtonImage();
      });
    this.subscriptions.add(breakPointSub);

    this.dynamicCSSObj.commandBarCSS = this.configService.clientConfig().commandBarCSS || this.defaultCommandBarCSS;

    this.setCommandBarCSS();

    this.enableDarkMode = this.configService.clientConfig().form?.enableDarkMode === true;

    const dataSub = this.route.data.subscribe(data => {
      this.noNavBar = data.noNavBar;
      const initialized = this.localStorageService.getItem(LocalStorageKeys.TOP_BAR_INITIALIZED);
      this.initialized = initialized === 'true' ? true : false;
    });

    this.broadcastService.on(ClientConstants.DOCUMENT_LOADED).subscribe((data: any) => {
      if (data && data.documentData && data.documentData.template) {
        const template = data.documentData.template;
        const currentMainTemplate = this.currentDocumentService.getTemplate();

        if (template.documentId === currentMainTemplate?.documentId) {
          this.hideTopBar = template.hideTopBar === true;
          this.fullWindow = template.fullWindow === true;
          this.noNavBar = template.noNavBar === true;
          this.hideGeneralSearch = template.navigationBar?.hideGeneralSearch === true;
        }

        const storedCommandBarCSS = this.modifiedCSSService.getStoredCommandBarCSS(template.documentId);
        this.setCommandBarCSS(storedCommandBarCSS);
      }

      if (!this.initialized) {
        this.initialized = true;
        this.cd.detectChanges();
      }
    });


    this.hideGeneralSearch = this.configService.clientConfig().navigationBar?.hideGeneralSearch === true;

    const imagesSub = this.navigationService.images$.subscribe(images => {
      this.templateImages = images;

      if (images.homeButtonImage) {
        this.homeButtonImage = images.homeButtonImage
      }
      this.setHomeButtonImage();
    });

    this.subscriptions.add(dataSub);
    this.subscriptions.add(imagesSub);

    this.weedLink = serverRoutes.loadFile;

    const MAX_WIDTH = 51;
    const WIDTH_OFFSET = 9;
    const DOCHEADER_INITIAL_WIDTH = 22;
    let docheaderWidth = DOCHEADER_INITIAL_WIDTH;

    this.imageLinkService.imageLink$.subscribe((imageLink) => {
      this.docInfo.imageLink = imageLink;

      if (this.docInfo.imageLink.imageFileNo96) {
        docheaderWidth = MAX_WIDTH + WIDTH_OFFSET;
        this.imageGalleryService.addImageToGallery(this.galleryId, this.weedLink + '/' + imageLink.imageFileNo96);
      } else {
        docheaderWidth = 0;
      }

      this.docHeaderWidth = {
        'margin-right': docheaderWidth
      };
      this.getCommandBarImage();
    });

    const deviceWidth = (<any>window).innerWidth;
    if (deviceWidth > SIDEBAR_OPEN_MIN_WIDTH) {
      this.sideBarState = await this.controlStateSaveService.getControlState(ControlStateConstants.CONTROL_STATE_SIDEBAR_TOGGLE);
    }

    if (!this.sideBarState) {
      this.sideBarState = {
        leftSideBarOpen: false
      };
    }
  }

  toggleSideNav() {
    this.sideBarState.leftSideBarOpen = !this.sideBarState.leftSideBarOpen;
    this.controlStateSaveService.setControlState(ControlStateConstants.CONTROL_STATE_SIDEBAR_TOGGLE, this.sideBarState);
    this.snav.toggle();
  }


  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.mobileQuery.removeListener(this._mobileQueryListener);
    this.compactQuery.removeListener(this._compactQueryListener);
  }

  changeState() {
    if (this.defaultStateService.isPathAccountDefault()) {
      document.getElementById('ftForm').scrollIntoView(true);
    }
    this.defaultStateService.changeToDefaultState();
  }

  onMenuSelect() {
    const deviceWidth = (<any>window).innerWidth;
    if (deviceWidth <= SIDEBAR_OPEN_MIN_WIDTH) {
      this.toggleSideNav();
    }
  }

  viewImageFile() {
    this.imageGalleryService.open(this.galleryId, this.weedLink + '/' + this.docInfo.imageLink.imageFileNo96);
  }

  setHomeButtonImage() {
    if (this.breakpointState) {
      if (this.breakpointState.matches) {
        this.homeButtonImage = this.templateImages?.homeButtonImages?.breakpoints?.xs
          || this.configService.clientConfig().homeButtonImages?.breakpoints?.xs
          || this.homeButtonImage;
      } else {
        this.homeButtonImage = this.templateImages?.homeButtonImages?.breakpoints?.lg
          || this.configService.clientConfig().homeButtonImages?.breakpoints?.lg
          || this.homeButtonImage;
      }
    }
    this.getHomeButtonImage();
  }

  getHomeButtonImage() {
    if (!this.homeButtonImage) {
      return
    }
    const file = { fileNo: this.homeButtonImage };
    this.fileProviderService.getFileResources(file).then((resources: any) => {
      this.fileResources = resources;
    });
  }
  getCommandBarImage() {
    if (this.docInfo?.imageLink?.imageFileNo96) {
      const file = { fileNo: this.docInfo?.imageLink?.imageFileNo96 };
      if (!file.fileNo) {
        return
      }
      this.fileProviderService.getFileResources(file).then((resources: any) => {
        this.fileResourcesCommandBar = resources;
      });
    }
  }
}
